import { CompliancePdfPreview } from '../CompliancePdfPreview';
import { RrcCompanyProfileInspector } from '../company-profiles/RrcCompanyProfileInspector';
import { RrcLocationProfileInspector } from '../location-profiles/RrcLocationProfileInspector';
import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  extractErrorMessage,
  useGetRrcEp9WasteProfileQuery,
  useGetCurrentRrcEp9WasteProfileByKeyQuery,
  useGetRrcEp9WasteProfilesByKeyQuery,
  useCreateRrcEp9WasteProfileMutation,
  useUpdateRrcEp9WasteProfileMutation,
  useDeleteRrcEp9WasteProfileMutation,
  type RrcEp9WasteProfile,
  type RrcEp9WasteProfileCreateDTO,
  type RrcEp9WasteProfileUpdateDTO,
} from '@ots-frontend/ots-data';
import { useAuth } from '../../app/AuthProvider';
import { useApp, useAppDensity } from '../../app/AppContext';
import { OtsDialog } from '../../components/OtsDialog';
import { OtsThemeRoot } from '../../theme/OtsTheme';
import { AdminPage } from '../../components/AdminPage';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsDataTableEmpty } from '../../components/OtsDataTableCells';
import { ConfirmationDialog } from '../../dialogs/ConfirmationDialog';
import { RrcEp9WasteProfileView } from './RrcEp9WasteProfileView';
import { RrcEp9WasteProfileForm } from './RrcEp9WasteProfileForm';

export type RrcEp9WasteProfileInspectorProps = {
  id?: string;
  profileKey?: string;
  tenantCode?: string;
  readOnly?: boolean;
  initialMode?: 'create' | 'edit' | 'copy';
  defaults?: Partial<RrcEp9WasteProfile>;
  objectLocked?: boolean;
  presentation?: 'dialog' | 'embedded';
  compact?: boolean;
  onClose?: () => void;
  onChanged?: (profile?: RrcEp9WasteProfile) => void;
};
const Inspector = ({
  id,
  profileKey,
  tenantCode,
  readOnly: readOnlyProp = false,
  initialMode,
  defaults,
  objectLocked,
  presentation = 'dialog',
  compact: compactProp,
  onClose,
  onChanged,
}: RrcEp9WasteProfileInspectorProps) => {
  const auth = useAuth();
  const app = useApp();
  const density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const [companyProfileId, setCompanyProfileId] = useState<string>();
  const [locationProfileId, setLocationProfileId] = useState<string>();
  const [mode, setMode] = useState(initialMode);
  const [activeId, setActiveId] = useState(id);
  const [editing, setEditing] = useState<RrcEp9WasteProfile>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [pendingDelete, setPendingDelete] = useState<RrcEp9WasteProfile>();
  const [deleted, setDeleted] = useState(false);
  const canView = auth.hasAccess('rrcep9wasteprofile_view');
  const canCreate = auth.hasAccess('rrcep9wasteprofile_create');
  // The established mutation endpoints operate on the current tenant only. Explicitly scoped reads remain view-only.
  const readOnly = readOnlyProp || !!tenantCode;
  const byId = useGetRrcEp9WasteProfileQuery(
    canView && !deleted && activeId ? { id: activeId, tenantCode } : skipToken
  );
  const byKey = useGetCurrentRrcEp9WasteProfileByKeyQuery(
    canView && !deleted && !activeId && profileKey
      ? { profileKey, tenantCode }
      : skipToken
  );
  const query = activeId ? byId : byKey;
  const profile = query.currentData;
  const access = (value: RrcEp9WasteProfile, permission: string) =>
    auth.hasAccess(value, permission);
  const versions = useGetRrcEp9WasteProfilesByKeyQuery(
    canView && profile && !mode && !deleted
      ? { profileKey: profile.profileKey, tenantCode }
      : skipToken
  );
  const [create, creating] = useCreateRrcEp9WasteProfileMutation();
  const [update, updating] = useUpdateRrcEp9WasteProfileMutation();
  const [remove, removing] = useDeleteRrcEp9WasteProfileMutation();
  const busy = creating.isLoading || updating.isLoading || removing.isLoading;
  const openEditor = (value: RrcEp9WasteProfile, next: 'edit' | 'copy') => {
    if (readOnly) return;
    setEditing(value);
    setMode(next);
    setError(undefined);
    setNotice(undefined);
  };
  const closeEditor = () => {
    if (busy) return;
    if (initialMode) onClose?.();
    else {
      setMode(undefined);
      setEditing(undefined);
      setError(undefined);
    }
  };
  const save = async (
    payload: RrcEp9WasteProfileCreateDTO & { id?: string }
  ) => {
    const target = editing ?? profile;
    if (
      busy ||
      readOnly ||
      (mode === 'edit'
        ? !target || !access(target, 'rrcep9wasteprofile_edit')
        : !canCreate)
    )
      return;
    setError(undefined);
    try {
      const saved = await (mode === 'edit'
        ? update(payload as RrcEp9WasteProfileUpdateDTO)
        : create(payload)
      ).unwrap();
      setActiveId(saved.id);
      setMode(undefined);
      setEditing(undefined);
      setNotice('EP-9 waste profile saved.');
      onChanged?.(saved);
      if (initialMode) onClose?.();
    } catch (reason) {
      setError(
        extractErrorMessage(
          reason,
          'The EP-9 waste profile could not be saved.'
        )
      );
    }
  };
  const confirmDelete = async () => {
    if (
      !pendingDelete ||
      busy ||
      readOnly ||
      !access(pendingDelete, 'rrcep9wasteprofile_delete')
    )
      return;
    setError(undefined);
    try {
      await remove({ id: pendingDelete.id }).unwrap();
      setPendingDelete(undefined);
      setDeleted(true);
      onChanged?.();
      onClose?.();
    } catch (reason) {
      setError(
        extractErrorMessage(
          reason,
          'The EP-9 waste profile could not be deleted.'
        )
      );
    }
  };
  const title =
    mode === 'create'
      ? 'New RRC EP-9 waste profile'
      : mode === 'copy'
      ? 'Copy RRC EP-9 waste profile'
      : mode === 'edit'
      ? 'Edit RRC EP-9 waste profile'
      : 'RRC EP-9 waste profile';
  let content;
  if (deleted)
    content = (
      <OtsInlineAlert tone="success" title="EP-9 waste profile deleted" />
    );
  else if ((mode === 'create' && !canCreate) || (mode !== 'create' && !canView))
    content = (
      <OtsInlineAlert title="Access denied" tone="warning">
        You do not have permission to view or manage this EP-9 waste profile.
      </OtsInlineAlert>
    );
  else if (query.isFetching && !profile && (activeId || profileKey))
    content = (
      <OtsDataTableEmpty loading loadingText="Loading EP-9 waste profile…">
        {null}
      </OtsDataTableEmpty>
    );
  else if (query.error)
    content = (
      <OtsInlineAlert
        tone="danger"
        title="EP-9 waste profile unavailable"
        action={
          <OtsActionButton size="compact" onClick={() => void query.refetch()}>
            Try again
          </OtsActionButton>
        }
      >
        {extractErrorMessage(
          query.error,
          'The EP-9 waste profile could not be loaded.'
        )}
      </OtsInlineAlert>
    );
  else if (profile && !access(profile, 'rrcep9wasteprofile_view'))
    content = (
      <OtsInlineAlert tone="warning" title="Access denied">
        You do not have permission to view this record.
      </OtsInlineAlert>
    );
  else if (
    mode &&
    (readOnly ||
      (mode === 'edit'
        ? !profile || !access(editing ?? profile, 'rrcep9wasteprofile_edit')
        : !canCreate))
  )
    content = (
      <OtsInlineAlert tone="warning" title="View only">
        This profile cannot be modified from this view.
      </OtsInlineAlert>
    );
  else if (mode)
    content = (
      <RrcEp9WasteProfileForm
        key={`${mode}-${editing?.id ?? profile?.id ?? 'new'}`}
        mode={mode}
        profile={editing ?? profile ?? defaults}
        objectLocked={objectLocked}
        regionId={app?.regionId}
        compact={compact}
        submitting={busy}
        error={error}
        onSubmit={(payload) => void save(payload)}
        onCancel={closeEditor}
      />
    );
  else if (!profile)
    content = (
      <OtsInlineAlert title="EP-9 waste profile not found">
        Choose a EP-9 waste profile to inspect.
      </OtsInlineAlert>
    );
  else
    content = (
      <>
        {notice && <OtsInlineAlert tone="success" title={notice} />}
        {error && !pendingDelete && (
          <OtsInlineAlert tone="danger">{error}</OtsInlineAlert>
        )}
        <RrcEp9WasteProfileView
          key={profile.id}
          profile={profile}
          versions={(versions.currentData ?? []).filter((value) =>
            access(value, 'rrcep9wasteprofile_view')
          )}
          loadingVersions={versions.isFetching}
          versionsError={
            versions.error
              ? extractErrorMessage(
                  versions.error,
                  'Version history could not be loaded.'
                )
              : undefined
          }
          onRetry={() => void versions.refetch()}
          readOnly={readOnly}
          compact={compact}
          onOpenCompanyProfile={setCompanyProfileId}
          onOpenLocationProfile={setLocationProfileId}
          renderPdf={(selected) => (
            <CompliancePdfPreview
              document={{ program: 'ep9', id: selected.id, tenantCode }}
              label={`EP-9 PDF · Waste ${selected.wasteId} · Version ${selected.version}`}
              compact={compact}
            />
          )}
          onCopy={canCreate ? (value) => openEditor(value, 'copy') : undefined}
          onEdit={(value) => openEditor(value, 'edit')}
          canEdit={(value) => access(value, 'rrcep9wasteprofile_edit')}
          onDelete={(value) => {
            setPendingDelete(value);
            setError(undefined);
          }}
          canDelete={(value) => access(value, 'rrcep9wasteprofile_delete')}
        />
      </>
    );
  const body = (
    <>
      {content}
      {companyProfileId && (
        <RrcCompanyProfileInspector
          id={companyProfileId}
          readOnly={readOnly}
          tenantCode={tenantCode}
          compact={compact}
          onClose={() => setCompanyProfileId(undefined)}
        />
      )}
      {locationProfileId && (
        <RrcLocationProfileInspector
          id={locationProfileId}
          readOnly={readOnly}
          tenantCode={tenantCode}
          compact={compact}
          onClose={() => setLocationProfileId(undefined)}
        />
      )}
      {pendingDelete && (
        <ConfirmationDialog
          open
          title="Delete EP-9 waste profile?"
          tone="danger"
          description={`Delete version ${pendingDelete.version} of ${
            pendingDelete.wasteId ?? 'this EP-9 waste profile'
          }?`}
          confirmLabel="Delete profile"
          isConfirming={removing.isLoading}
          onConfirm={() => void confirmDelete()}
          onCancel={() => {
            if (!busy) {
              setPendingDelete(undefined);
              setError(undefined);
            }
          }}
          density={compact ? 'compact' : 'default'}
        >
          Referenced profiles may be protected from deletion by the server.
          {error && (
            <OtsInlineAlert className="mt-3" tone="danger">
              {error}
            </OtsInlineAlert>
          )}
        </ConfirmationDialog>
      )}
    </>
  );
  return presentation === 'dialog' ? (
    <OtsDialog
      open
      title={title}
      compact={compact}
      divided
      description={profile?.objectName ?? defaults?.objectName}
      onClose={() => {
        if (!busy) onClose?.();
      }}
    >
      {body}
    </OtsDialog>
  ) : (
    <OtsThemeRoot>
      <AdminPage
        title={title}
        fullWidth
        density={compact ? 'compact' : 'default'}
        actions={
          onClose && (
            <OtsActionButton onClick={onClose} disabled={busy} size="compact">
              Close
            </OtsActionButton>
          )
        }
      >
        <div className="mt-4">{body}</div>
      </AdminPage>
    </OtsThemeRoot>
  );
};

/** Identity changes reset unsaved state and selected history, including when the tenant changes. */
export const RrcEp9WasteProfileInspector = (
  props: RrcEp9WasteProfileInspectorProps
) => (
  <Inspector
    key={JSON.stringify([
      props.id,
      props.profileKey,
      props.tenantCode,
      props.initialMode,
    ])}
    {...props}
  />
);
